import { useEffect, useState } from 'react';
import { Link } from 'react-router-dom';
import supervisorService from '../../services/supervisorService';
import LoadingSpinner from '../../components/LoadingSpinner';
import TeamMembersModal from '../../components/TeamMembersModal';

export default function SupervisorDashboard() {
  const [loading, setLoading] = useState(true);
  const [overview, setOverview] = useState(null);
  const [showTeamMembers, setShowTeamMembers] = useState(false);

  useEffect(() => {
    const fetchOverview = async () => {
      try {
        const response = await supervisorService.getOverview();
        setOverview(response.data);
      } catch (error) {
        console.error('Failed to fetch overview:', error);
      } finally {
        setLoading(false);
      }
    };

    fetchOverview();
  }, []);

  if (loading) {
    return (
      <div className="flex items-center justify-center h-64">
        <LoadingSpinner size="lg" />
      </div>
    );
  }

  return (
    <div className="space-y-6">
      <h1 className="text-2xl font-bold text-gray-900">Team Overview</h1>

      {/* Stats Cards */}
      <div className="grid grid-cols-1 md:grid-cols-4 gap-4">
        <div className="bg-white rounded-lg shadow-md p-6">
          <div className="text-sm text-gray-600">Total Drivers</div>
          <div className="text-3xl font-bold text-primary-600">{overview?.totalDrivers || 0}</div>
        </div>
        <div className="bg-white rounded-lg shadow-md p-6">
          <div className="text-sm text-gray-600">Active Today</div>
          <div className="text-3xl font-bold text-green-600">{overview?.activeDrivers || 0}</div>
        </div>
        <Link to="/supervisor/approvals" className="bg-white rounded-lg shadow-md p-6 hover:shadow-lg transition-shadow">
          <div className="text-sm text-gray-600">Pending Approvals</div>
          <div className="text-3xl font-bold text-yellow-600">{overview?.pendingApprovals || 0}</div>
          <div className="text-xs text-primary-600 mt-1">Review now</div>
        </Link>
        <div className="bg-white rounded-lg shadow-md p-6">
          <div className="text-sm text-gray-600">Today's Logs</div>
          <div className="text-3xl font-bold text-blue-600">{overview?.todaysLogs || 0}</div>
        </div>
      </div>

      {/* Quick Actions */}
      <div className="bg-white rounded-lg shadow-md p-6">
        <h2 className="text-lg font-semibold mb-4">Quick Actions</h2>
        <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
          <Link
            to="/supervisor/approvals"
            className="p-4 border rounded-lg hover:bg-gray-50 transition-colors flex items-center"
          >
            <div className="w-10 h-10 bg-yellow-100 rounded-full flex items-center justify-center mr-4">
              <svg className="w-5 h-5 text-yellow-600" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 12l2 2 4-4m6 2a9 9 0 11-18 0 9 9 0 0118 0z" />
              </svg>
            </div>
            <div>
              <div className="font-medium">Review Approvals</div>
              <div className="text-sm text-gray-500">{overview?.pendingApprovals || 0} pending</div>
            </div>
          </Link>

          <Link
            to="/supervisor/team-kpis"
            className="p-4 border rounded-lg hover:bg-gray-50 transition-colors flex items-center"
          >
            <div className="w-10 h-10 bg-green-100 rounded-full flex items-center justify-center mr-4">
              <svg className="w-5 h-5 text-green-600" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 19v-6a2 2 0 00-2-2H5a2 2 0 00-2 2v6a2 2 0 002 2h2a2 2 0 002-2zm0 0V9a2 2 0 012-2h2a2 2 0 012 2v10m-6 0a2 2 0 002 2h2a2 2 0 002-2m0 0V5a2 2 0 012-2h2a2 2 0 012 2v14a2 2 0 01-2 2h-2a2 2 0 01-2-2z" />
              </svg>
            </div>
            <div>
              <div className="font-medium">Team KPIs</div>
              <div className="text-sm text-gray-500">View performance</div>
            </div>
          </Link>

          <button
            onClick={() => setShowTeamMembers(true)}
            className="p-4 border rounded-lg hover:bg-gray-50 transition-colors flex items-center text-left w-full"
          >
            <div className="w-10 h-10 bg-blue-100 rounded-full flex items-center justify-center mr-4">
              <svg className="w-5 h-5 text-blue-600" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 4.354a4 4 0 110 5.292M15 21H3v-1a6 6 0 0112 0v1zm0 0h6v-1a6 6 0 00-9-5.197M13 7a4 4 0 11-8 0 4 4 0 018 0z" />
              </svg>
            </div>
            <div>
              <div className="font-medium">Team Members</div>
              <div className="text-sm text-gray-500">{overview?.totalDrivers || 0} drivers</div>
            </div>
          </button>
        </div>
      </div>

      {/* Team Members Modal */}
      <TeamMembersModal
        isOpen={showTeamMembers}
        onClose={() => setShowTeamMembers(false)}
      />
    </div>
  );
}
